// frontend/src/components/VoiceSelector.tsx
import { getVoiceOptGroups, getVoiceName } from '@/constants/voices';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Zap, Volume2 } from 'lucide-react';

interface VoiceSelectorProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
}

export function VoiceSelector({ value, onChange, className }: VoiceSelectorProps) {
    const groups = getVoiceOptGroups(); // ✅ Use plan-filtered groups

    const getVoiceIcon = (type: string) => {
        switch (type) {
            case 'neural2':
                return <Sparkles className="w-4 h-4 text-purple-500" />;
            case 'wavenet':
                return <Zap className="w-4 h-4 text-blue-500" />;
            default:
                return <Volume2 className="w-4 h-4 text-gray-400" />;
        }
    };

    return (
        <div className={className}>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a voice">
                        {value && (
                            <div className="flex items-center gap-2">
                                {getVoiceIcon(
                                    groups.flatMap(g => g.options).find(v => v.id === value)?.type || ''
                                )}
                                <span>{getVoiceName(value)}</span>
                            </div>
                        )}
                    </SelectValue>
                </SelectTrigger>

                <SelectContent className="max-h-[400px]">
                    {groups.map(group => {

                        return (
                            <SelectGroup key={group.label}>
                                <SelectLabel className="flex items-center justify-between px-2 py-2">
                                    <span className="text-sm font-semibold">{group.label}</span>
                                    {group.badge && (
                                        <Badge
                                            variant="secondary"
                                            className="ml-2 bg-purple-100 text-purple-700"
                                        >
                                            {group.badge}
                                        </Badge>
                                    )}
                                </SelectLabel>
                                {group.description && (
                                    <p className="px-2 pb-2 text-xs text-gray-500">{group.description}</p>
                                )}
                                {group.options.map(voice => (
                                    <SelectItem
                                        key={voice.id}
                                        value={voice.id}
                                        className="cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            {getVoiceIcon(voice.type)}
                                            <span>{voice.name}</span>
                                        </div>
                                    </SelectItem>
                                ))}

                            </SelectGroup>
                        );
                    })}
                </SelectContent>
            </Select>

            {value && (
                <p className="text-xs text-gray-500 mt-1.5">
                    {groups.flatMap(g => g.options).find(v => v.id === value)?.type === 'neural2' && (
                        <span className="text-purple-600">✨ Most natural voice</span>
                    )}
                    {groups.flatMap(g => g.options).find(v => v.id === value)?.type === 'wavenet' && (
                        <span className="text-blue-600">⚡ High quality synthetic voice</span>
                    )}
                    {groups.flatMap(g => g.options).find(v => v.id === value)?.type === 'standard' && (
                        <span className="text-gray-600">Standard quality voice</span>
                    )}
                </p>
            )}

        </div>
    );
}